import React, { useState, useEffect, ReactNode } from "react";
import { StyleSheet, View, Pressable } from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import Button from "./Button";

interface BottomButtonProps {
  onPress?: (paymentMode: string) => void;
  title?: string;
  disable?: boolean;
  buttons: { Icon: any; text: string; paymentMode: "COD" | "PREPAID" }[];
  amount?: string;
  online_payment_metadata?: {
    payment_status:
      | "INITIAL"
      | "CREATED"
      | "FAILED"
      | "PENDING"
      | "SUCCESS_OTP"
      | "SUCCESS_WITHOUT_OTP"
      | "SUCCESS";
    is_marking_enabled: boolean;
    time_left: number;
    amount: string;
    failed_text?: string;
    is_otp_enabled: boolean;
  };
  handleNavigation?: (paymentMode: string) => void;
  refresh?: () => void;
  allow_delivery_for_prepaid_orders: boolean;
  show_delivery_btn_post_payment: boolean;
  is_otp_enabled: boolean;
  topAccessory?: ReactNode;
}

const formatTime = (timeInSeconds: number): string => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const BottomButton: React.FC<BottomButtonProps> = ({
  onPress = () => {},
  buttons = [],
  disable = false,
  amount = "0",
  online_payment_metadata = {
    payment_status: "INITIAL",
    is_marking_enabled: false,
    time_left: 30,
    amount: "0",
    failed_text: "",
    is_otp_enabled: false,
  },
  handleNavigation = () => {},
  refresh = () => {},
  show_delivery_btn_post_payment = false,
  allow_delivery_for_prepaid_orders = false,
  is_otp_enabled = false,
  topAccessory = null,
}) => {
  const [timeLeft, setTimeLeft] = useState(
    online_payment_metadata?.time_left ?? 30,
  );
  useEffect(() => {
    setTimeLeft(online_payment_metadata?.time_left ?? 30);
  }, [online_payment_metadata?.time_left]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (online_payment_metadata.payment_status === "CREATED" && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [online_payment_metadata.payment_status, timeLeft, refresh]);

  const renderPaymentContent = () => {
    const { payment_status, is_marking_enabled } = online_payment_metadata;

    switch (payment_status) {
      case "CREATED":
        return (
          <View style={styles.absoluteContainer}>
            {topAccessory}
            <View style={[styles.header, styles.headerNoMargin]}>
              <Text variant="subtitle_16">कस्टमर से पेमेंट लेना है</Text>
              <Text variant="heading_24">₹{amount}</Text>
            </View>
            {timeLeft > 0 ? (
              <Text variant="caption_4" style={styles.timerText}>
                आप कैश पेमेंट {formatTime(timeLeft)} मिनट बाद ले सकते हैं !
              </Text>
            ) : null}
            <View style={styles.buttonRow}>
              {buttons.map(({ Icon, text, paymentMode }) => (
                <Pressable
                  style={[
                    styles.btnContainer,
                    paymentMode === "COD" &&
                      !is_marking_enabled && {
                        backgroundColor: color.black02,
                        elevation: 0,
                        borderColor: color.black02,
                      },
                  ]}
                  testID="qrPaymentBtn"
                  onPress={() =>
                    paymentMode === "PREPAID" && onPress(paymentMode)
                  }
                  disabled={paymentMode === "COD"}
                  key={paymentMode}
                >
                  <Icon />
                  <Text variant="title_16" style={styles.buttonText}>
                    {text}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>
        );
      case "PENDING":
        return (
          <View style={styles.absoluteContainer}>
            {topAccessory}
            <View style={[styles.header, styles.headerBaseline]}>
              <Text variant="subtitle_16">कस्टमर का </Text>
              <Text variant="title_20">₹{online_payment_metadata.amount}</Text>
              <Text variant="subtitle_16"> ऑनलाइन पेमेंट पेंडिंग में है.</Text>
            </View>
            <Button
              title={"कृपया कुछ देर प्रतीक्षा करें..."}
              disabled={true}
              disabledColors={false}
              backgroundColor={"rgba(0, 0, 0, 0.05)"}
              btnColor={"rgba(0, 0, 0, 0.05)"}
              textColor={"#2F78EC"}
              onPress={() => {}}
            />
          </View>
        );
      case "SUCCESS":
        return (
          <View style={styles.absoluteContainer}>
            {topAccessory}
            <View style={[styles.header, styles.headerBaseline]}>
              <Text variant="subtitle_16">कस्टमर से </Text>
              <Text variant="title_20">₹{online_payment_metadata.amount}</Text>
              <Text variant="subtitle_16"> का पेमेंट ऑनलाइन प्राप्त हुआ !</Text>
            </View>
            {show_delivery_btn_post_payment ? (
              <Button
                title={"Submit"}
                disabled={false}
                disabledColors={false}
                backgroundColor={"rgba(25, 129, 85, 1)"}
                btnColor={"rgba(25, 129, 85, 1)"}
                textColor={color.white}
                onPress={() => handleNavigation("PREPAID")}
              />
            ) : null}
          </View>
        );

      case "SUCCESS_OTP":
        return (
          <View style={styles.absoluteContainer}>
            {topAccessory}
            <View style={[styles.header, styles.headerBaseline]}>
              <Text variant="subtitle_16">कस्टमर से </Text>
              <Text variant="heading_24">
                ₹{online_payment_metadata.amount}
              </Text>
              <Text variant="subtitle_16"> का पेमेंट ऑनलाइन प्राप्त हुआ !</Text>
            </View>
            {show_delivery_btn_post_payment ? (
              <Button
                title={"OTP वेरीफाई करें"}
                disabled={false}
                disabledColors={false}
                backgroundColor={"rgba(25, 129, 85, 1)"}
                btnColor={"rgba(25, 129, 85, 1)"}
                textColor={color.white}
                onPress={() => handleNavigation("PREPAID")}
              />
            ) : null}
          </View>
        );

      default:
        return (
          <View style={styles.absoluteContainer}>
            {topAccessory}
            {allow_delivery_for_prepaid_orders ? null : (
              <View style={styles.header}>
                <Text variant="subtitle_16">कस्टमर से पेमेंट लेना है</Text>
                <Text variant="heading_24">₹{amount}</Text>
              </View>
            )}
            {!allow_delivery_for_prepaid_orders &&
            online_payment_metadata?.failed_text ? (
              <View style={styles.failedTextContainer}>
                <Text variant="body_text_3" style={{ color: color.plain_red }}>
                  {online_payment_metadata?.failed_text}
                </Text>
              </View>
            ) : null}
            {allow_delivery_for_prepaid_orders ? (
              <Button
                title={
                  online_payment_metadata.is_otp_enabled || is_otp_enabled
                    ? "OTP वेरीफाई करें"
                    : "Submit"
                }
                disabled={disable}
                disabledColors={disable}
                backgroundColor={"rgba(25, 129, 85, 1)"}
                btnColor={disable ? "" : "rgba(25, 129, 85, 1)"}
                textColor={color.white}
                onPress={() => handleNavigation("PREPAID")}
              />
            ) : (
              <View style={styles.buttonRow}>
                {buttons.map(({ Icon, text, paymentMode }) => (
                  <Pressable
                    style={[
                      styles.btnContainer,
                      disable && {
                        backgroundColor: color.black02,
                        elevation: 0,
                        borderColor: color.black02,
                      },
                    ]}
                    testID="qrPaymentBtn"
                    onPress={() => onPress(paymentMode)}
                    disabled={disable}
                    key={paymentMode}
                  >
                    <Icon />
                    <Text
                      variant="title_16"
                      style={[
                        styles.buttonText,
                        disable && {
                          color: color.black06,
                        },
                      ]}
                    >
                      {text}
                    </Text>
                  </Pressable>
                ))}
              </View>
            )}
          </View>
        );
    }
  };

  return renderPaymentContent();
};

export default BottomButton;

const styles = StyleSheet.create({
  absoluteContainer: {
    backgroundColor: color.white,
    position: "absolute",
    bottom: 0,
    width: "100%",
    padding: 16,
    elevation: 10,
    paddingTop: 12,
  },
  header: {
    marginBottom: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerNoMargin: {
    marginBottom: 0,
  },
  headerBaseline: {
    alignItems: "baseline",
    justifyContent: "flex-start",
  },
  timerText: {
    marginBottom: 4,
    top: -2,
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  btnContainer: {
    paddingVertical: 10,
    alignItems: "center",
    borderRadius: 8,
    flexDirection: "row",
    justifyContent: "center",
    flex: 1,
    marginHorizontal: 4,
    borderColor: color.black,
    borderWidth: 1,
    elevation: 4,
    backgroundColor: color.white,
  },
  buttonText: {
    marginLeft: 10,
    color: color.black,
  },
  failedTextContainer: {
    alignItems: "center",
    backgroundColor: color.white,
    borderColor: color.plain_red,
    borderWidth: 1,
    borderRadius: 8,
    flex: 1,
    paddingVertical: 2,
    position: "absolute",
    paddingHorizontal: 8,
    top: -40,
    alignSelf: "center",
  },
});
